/**
 * Custom error type for API fetch operations
 */
export class ApiError extends Error {
  status: number;
  statusText: string;
  data?: {
    message: string;
    text: string;
  };

  constructor(status: number, statusText: string, message: string, data?: any) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.statusText = statusText;
    this.data = data;

    // This is needed in TypeScript to ensure prototype chain works correctly
    Object.setPrototypeOf(this, ApiError.prototype);
  }

  static async fromResponse(response: Response): Promise<ApiError> {
    let data: any;
    let message: string;

    try {
      data = await response.json();
      message = data.message || response.statusText;
    } catch (e) {
      data = null;
      message = response.statusText;
    }

    return new ApiError(response.status, response.statusText, message, data);
  }
}

/**
 * Error type for network connectivity issues
 */
export class NetworkError extends Error {
  constructor(message: string = "Network error occurred") {
    super(message);
    this.name = "NetworkError";

    // This is needed in TypeScript to ensure prototype chain works correctly
    Object.setPrototypeOf(this, NetworkError.prototype);
  }
}

/**
 * Error type for request timeout
 */
export class TimeoutError extends Error {
  constructor(message: string = "Request timed out") {
    super(message);
    this.name = "TimeoutError";

    // This is needed in TypeScript to ensure prototype chain works correctly
    Object.setPrototypeOf(this, TimeoutError.prototype);
  }
}

/**
 * Error thrown when there's a problem with request parameters
 */
export class RequestError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "RequestError";

    // This is needed in TypeScript to ensure prototype chain works correctly
    Object.setPrototypeOf(this, RequestError.prototype);
  }
}

/**
 * Helper to determine if an error is a specific API error code
 */
export function isApiError(error: any): error is ApiError {
  return error instanceof ApiError;
}

/**
 * Helper to determine if an error is a 401 Unauthorized error
 */
export function isUnauthorizedError(error: any): boolean {
  return isApiError(error) && error.status === 401;
}

/**
 * Helper to determine if an error is a 403 Forbidden error
 */
export function isForbiddenError(error: any): boolean {
  return isApiError(error) && error.status === 403;
}

/**
 * Helper to determine if an error is a 404 Not Found error
 */
export function isNotFoundError(error: any): boolean {
  return isApiError(error) && error.status === 404;
}

/**
 * Helper to determine if an error is a network connectivity issue
 */
export function isNetworkError(error: any): error is NetworkError {
  return error instanceof NetworkError;
}

/**
 * Helper to determine if a request timed out
 */
export function isTimeoutError(error: any): error is TimeoutError {
  return error instanceof TimeoutError;
}

/**
 * Extract a user-friendly error message from any error type
 */
export function getErrorMessage(error: any): string {
  if (isApiError(error)) {
    return error.message || `${error.status} ${error.statusText}`;
  }

  if (error instanceof Error) {
    return error.message;
  }

  // Handle API error objects with nested message structures
  if (error?.message) {
    return error.message;
  } else if (error?.data?.message) {
    return error.data.message;
  } else if (typeof error === "string") {
    return error;
  }

  return String(error);
}
